import _ from 'lodash';
import { useEffect, useImperativeHandle, useMemo, type ForwardedRef } from 'react';
import { SelectInputOption } from '@/shared/ui';
import { useMergeState } from '@/shared/lib/customHooks';
import { useReportStore } from '@/entities/report';
import { generateInitalCheckboxState, generateOptions } from './handler';
import type { ReportSettingsRef } from './index';

/**
 * Drives the PDF report settings panel — section checkboxes plus the gain and filter selects. Seeds state from the generated options and the empty checkbox map, reloads it from the saved report configuration whenever report data arrives, exposes the checkbox and select handlers, memoises the labelled checkbox rows, and publishes getReportConfiguration on the forwarded ref for the parent to read back.
 *
 * @param ref - Forwarded handle receiving getReportConfiguration.
 * @returns An object carrying the option lists, the settings state, both change handlers and the checkbox rows.
 */
const useReportSettings = (ref: ForwardedRef<ReportSettingsRef>) => {
  const { gainOptions, hpFilterOptions, lpFilterOptions } = generateOptions();

  const reportData = useReportStore((s) => s.reportData);

  const [state, setState] = useMergeState({
    checkboxState: generateInitalCheckboxState(),
    gain: _.find(gainOptions, { value: 10 }),
    hpFilter: _.find(hpFilterOptions, { value: 0.5 }),
    lpFilter: _.find(lpFilterOptions, { value: 40 }),
    longestRunBasedOn: 'duration',
  });

  /**
   * Toggles one section checkbox of the PDF report settings. Copies the checkbox state, writes the inverse of the value passed in under the given name, and merges it back into local state.
   *
   * @param name - Key of the report section being toggled.
   * @param value - Current value of the checkbox, which is inverted.
   * @returns Nothing.
   */
  const onChangeCheckbox = (name: string, value: boolean) => {
    const updatedCheckboxState = { ...state.checkboxState };
    (updatedCheckboxState as Record<string, boolean>)[name] = !value;
    setState({ checkboxState: updatedCheckboxState });
  };

  /**
   * Applies a dropdown change in the PDF report settings. Merges the new value into local state under the given field name.
   *
   * @param name - Field being set, such as gain, hpFilter or lpFilter.
   * @param value - Selected option or raw value to store.
   * @returns Nothing.
   */
  const onChangeSelect = (
    name: string,
    value: string | SelectInputOption | SelectInputOption[] | null,
  ) => {
    setState({ [name]: value });
  };

  useImperativeHandle(ref, () => ({
    getReportConfiguration: () => {
      const sections: string[] = [];
      _.forEach(state.checkboxState, (value, key) => {
        if (value) {
          sections.push(key);
        }
      });
      return {
        ecgViewer: {
          gain: state.gain?.value,
          hpFilter: state.hpFilter?.value,
          lpFilter: state.lpFilter?.value,
        },
        longestRunBasedOn: state.longestRunBasedOn || 'duration',
        sections,
      };
    },
  }));

  useEffect(() => {
    const { reportConfiguration } = reportData?.holterProfile || {};
    const { sections, ecgViewer, longestRunBasedOn } = reportConfiguration || {};

    const setStateObject: Record<string, any> = {};

    if (!_.isEmpty(sections)) {
      const clonedCheckboxState: Record<string, boolean> = generateInitalCheckboxState();
      _.forEach(sections, (section: string) => {
        clonedCheckboxState[section] = true;
      });
      _.assign(setStateObject, {
        checkboxState: clonedCheckboxState,
      });
    } else {
      _.assign(setStateObject, {
        checkboxState: generateInitalCheckboxState(),
      });
    }

    if (!_.isEmpty(ecgViewer)) {
      _.assign(setStateObject, {
        gain: _.find(gainOptions, { value: ecgViewer.gain }),
        hpFilter: _.find(hpFilterOptions, { value: ecgViewer.hpFilter }),
        lpFilter: _.find(lpFilterOptions, { value: ecgViewer.lpFilter }),
      });
    }

    _.assign(setStateObject, {
      longestRunBasedOn: ['beats', 'duration'].includes(longestRunBasedOn)
        ? longestRunBasedOn
        : 'duration',
    });

    setState(setStateObject);
  }, [reportData]);

  const checkboxRender = useMemo(
    () => [
      {
        name: 'tableOfContent',
        data: {
          label: 'Table of contents',
          value: state.checkboxState.tableOfContent,
        },
        checked: state.checkboxState.tableOfContent,
      },
      {
        name: 'summary',
        data: {
          label: 'Statistics page',
          value: state.checkboxState.summary,
        },
        checked: state.checkboxState.summary,
      },
      {
        name: 'dailySummary',
        data: {
          label: 'Daily summary',
          value: state.checkboxState.dailySummary,
        },
        checked: state.checkboxState.dailySummary,
      },
      {
        name: 'hrChart',
        data: {
          label: 'HR chart',
          value: state.checkboxState.hrChart,
        },
        checked: state.checkboxState.hrChart,
      },
      {
        name: 'afibAnalysis',
        data: {
          label: 'AFib burden analysis',
          value: state.checkboxState.afibAnalysis,
        },
        checked: state.checkboxState.afibAnalysis,
      },
      {
        name: 'veAnalysis',
        data: {
          label: 'VE burden analysis',
          value: state.checkboxState.veAnalysis,
        },
        checked: state.checkboxState.veAnalysis,
      },
      {
        name: 'sveAnalysis',
        data: {
          label: 'SVE burden analysis',
          value: state.checkboxState.sveAnalysis,
        },
        checked: state.checkboxState.sveAnalysis,
      },
      {
        name: 'stTrend',
        data: {
          label: 'ST Trend',
          value: state.checkboxState.stTrend,
        },
        checked: state.checkboxState.stTrend,
      },
      {
        name: 'hrvSummary',
        data: {
          label: 'HRV Summary - Trend',
          value: state.checkboxState.hrvSummary,
        },
        checked: state.checkboxState.hrvSummary,
      },
      {
        name: 'hrvRrInterval',
        data: {
          label: 'HRV - RR Interval',
          value: state.checkboxState.hrvRrInterval,
        },
        checked: state.checkboxState.hrvRrInterval,
      },
      {
        name: 'qtcCharts',
        data: {
          label: 'QTC Charts',
          value: state.checkboxState.qtcCharts,
        },
        checked: state.checkboxState.qtcCharts,
      },
      {
        name: 'ecgDisclosure',
        data: {
          label: 'ECG Disclosure',
          value: state.checkboxState.ecgDisclosure,
        },
        checked: state.checkboxState.ecgDisclosure,
      },
    ],
    [state.checkboxState],
  );

  return {
    gainOptions,
    hpFilterOptions,
    lpFilterOptions,
    state,
    onChangeCheckbox,
    onChangeSelect,
    checkboxRender,
  };
};

export default useReportSettings;
